/**
 * ─────────────────────────────────────────────────────────────
 *  Polyreels Studio – Wishlist  |  v1.0.0
 *  Shopify Theme: Craft 15.2.0
 *  Sprachen: DE (default) + EN (/en/)
 *  Abhängigkeiten: keine (Vanilla JS, localStorage)
 * ─────────────────────────────────────────────────────────────
 *
 *  Öffentliche API (window.PolyreelsWishlist):
 *    .add(product)        Produkt hinzufügen
 *    .remove(productId)   Produkt entfernen
 *    .toggle(product)     Hinzufügen / Entfernen
 *    .has(productId)      Boolean – ist das Produkt gespeichert?
 *    .count()             Anzahl gespeicherter Produkte
 *    .clear()             Wunschliste leeren
 *
 *  Events:
 *    document → 'wishlist:change'  { detail: { items, count } }
 */

class PolyreelsWishlist {
  constructor() {
    this.storageKey = 'polyreels_wishlist';
    this.locale     = document.documentElement.lang || 'de';
    this.items      = this._load();
    this._bindGlobalEvents();
  }

  /* ─── Storage ─────────────────────────────────────────────── */

  _load() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey)) || [];
    } catch {
      return [];
    }
  }

  _save() {
    localStorage.setItem(this.storageKey, JSON.stringify(this.items));
    this._updateCountBadges();
    this._dispatchChange();
  }

  /* ─── Öffentliche API ─────────────────────────────────────── */

  /**
   * product = {
   *   id, handle, title, vendor, price (cents as string),
   *   image (URL), url, variantId
   * }
   */
  add(product) {
    if (!this.has(product.id)) {
      this.items.push({ ...product, addedAt: Date.now() });
      this._save();
      return true;
    }
    return false;
  }

  remove(productId) {
    const before = this.items.length;
    this.items = this.items.filter(p => String(p.id) !== String(productId));
    if (this.items.length !== before) {
      this._save();
      return true;
    }
    return false;
  }

  toggle(product) {
    return this.has(product.id) ? this.remove(product.id) : this.add(product);
  }

  has(productId) {
    return this.items.some(p => String(p.id) === String(productId));
  }

  count() {
    return this.items.length;
  }

  clear() {
    this.items = [];
    this._save();
  }

  /* ─── Events ──────────────────────────────────────────────── */

  _dispatchChange() {
    document.dispatchEvent(new CustomEvent('wishlist:change', {
      detail: { items: this.items, count: this.count() },
    }));
  }

  _bindGlobalEvents() {
    document.addEventListener('click', e => {
      // Heart-Toggle auf Produktkarten / Produktseite
      const toggleBtn = e.target.closest('[data-wishlist-toggle]');
      if (toggleBtn) {
        e.preventDefault();
        this._handleToggle(toggleBtn);
        return;
      }

      // Entfernen-Button auf der Wunschlisten-Seite
      const removeBtn = e.target.closest('[data-wishlist-remove]');
      if (removeBtn) {
        e.preventDefault();
        this.remove(removeBtn.dataset.wishlistRemove);
        this._renderWishlistPage();
        return;
      }

      // AJAX In-den-Warenkorb auf der Wunschlisten-Seite
      const atcBtn = e.target.closest('[data-wishlist-atc]');
      if (atcBtn) {
        e.preventDefault();
        this._handleAddToCart(atcBtn);
        return;
      }
    });

    // Synchronisation über Browser-Tabs
    window.addEventListener('storage', e => {
      if (e.key === this.storageKey) {
        this.items = this._load();
        this._updateCountBadges();
        this._updateAllButtons();
      }
    });
  }

  /* ─── Toggle Handler ──────────────────────────────────────── */

  _handleToggle(btn) {
    const product = {
      id:        btn.dataset.productId,
      handle:    btn.dataset.productHandle,
      title:     btn.dataset.productTitle,
      vendor:    btn.dataset.productVendor || '',
      price:     btn.dataset.productPrice,
      image:     btn.dataset.productImage,
      url:       btn.dataset.productUrl,
      variantId: btn.dataset.variantId || null,
    };

    this.toggle(product);

    // Pulse nur auf das Icon, nicht den gesamten Button
    btn.classList.add('wishlist-btn--animating');
    setTimeout(() => btn.classList.remove('wishlist-btn--animating'), 600);

    this._updateButton(btn, this.has(product.id));

    // Pulse auf das Header-Icon
    const headerIcon = document.getElementById('wishlist-icon-bubble');
    if (headerIcon) {
      headerIcon.classList.remove('wishlist-icon--pulse');
      void headerIcon.offsetWidth; // Reflow für Re-trigger
      headerIcon.classList.add('wishlist-icon--pulse');
      setTimeout(() => headerIcon.classList.remove('wishlist-icon--pulse'), 450);
    }
  }

  /* ─── AJAX Add to Cart ────────────────────────────────────── */

  async _handleAddToCart(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const originalHtml = btn.innerHTML;
    btn.disabled = true;
    btn.classList.add('wishlist-atc--loading');
    btn.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="wishlist-spinner">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-dasharray="31.4" stroke-dashoffset="10"/>
      </svg>`;

    try {
      const res = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify({ id: variantId, quantity: 1 }),
      });

      if (!res.ok) throw new Error('Cart add failed');

      // Erfolg-Checkmark
      btn.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <polyline points="20 6 9 17 4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`;
      btn.classList.remove('wishlist-atc--loading');
      btn.classList.add('wishlist-atc--success');

      // Cart-Bubble via Craft Section Rendering aktualisieren
      fetch('/?section_id=cart-icon-bubble')
        .then(r => r.text())
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newBubble      = doc.querySelector('.cart-count-bubble');
          const existingBubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
          if (newBubble && existingBubble) existingBubble.innerHTML = newBubble.innerHTML;
        })
        .catch(() => {});

      setTimeout(() => {
        btn.innerHTML = originalHtml;
        btn.classList.remove('wishlist-atc--success');
        btn.disabled = false;
      }, 1800);

    } catch {
      btn.innerHTML = originalHtml;
      btn.classList.remove('wishlist-atc--loading');
      btn.disabled = false;
    }
  }

  /* ─── UI Helpers ──────────────────────────────────────────── */

  _t(key) {
    const strings = {
      de: {
        empty:       'Deine Wunschliste ist noch leer.',
        empty_cta:   'Jetzt entdecken',
        remove:      'Aus Wunschliste entfernen',
        add_to_cart: 'In den Warenkorb',
      },
      en: {
        empty:       'Your wishlist is empty.',
        empty_cta:   'Discover products',
        remove:      'Remove from wishlist',
        add_to_cart: 'Add to cart',
      },
    };
    const lang = this.locale.startsWith('en') ? 'en' : 'de';
    return strings[lang][key] || key;
  }

  _updateCountBadges() {
    const count = this.count();

    // Header-Bubble (cart-count-bubble Pattern)
    const bubble = document.getElementById('wishlist-count-bubble');
    const number = document.getElementById('wishlist-count-number');
    if (bubble) bubble.style.display = count > 0 ? 'flex' : 'none';
    if (number) number.textContent   = count < 100 ? count : '99+';

    // Filled-State auf Header-Icon
    const headerIcon = document.getElementById('wishlist-icon-bubble');
    if (headerIcon) headerIcon.classList.toggle('wishlist-has-items', count > 0);
  }

  _updateButton(btn, isActive) {
    btn.setAttribute('aria-pressed', isActive);
    btn.classList.toggle('wishlist-btn--active', isActive);
    const icon = btn.querySelector('.wishlist-icon');
    if (icon) icon.setAttribute('data-filled', isActive);
  }

  _updateAllButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
      this._updateButton(btn, this.has(btn.dataset.productId));
    });
  }

  _formatPrice(price) {
    const cents = parseInt(price, 10);
    if (isNaN(cents)) return price;
    return new Intl.NumberFormat(
      this.locale.startsWith('en') ? 'en-DE' : 'de-DE',
      { style: 'currency', currency: 'EUR' }
    ).format(cents / 100);
  }

  /* ─── Wunschlisten-Seite ──────────────────────────────────── */

  _renderWishlistPage() {
    const container = document.getElementById('wishlist-page-grid');
    if (!container) return;

    if (this.items.length === 0) {
      container.innerHTML = `
        <div class="wishlist-page-empty">
          <svg width="52" height="52" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="opacity:.18">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <p>${this._t('empty')}</p>
          <a href="${this.locale.startsWith('en') ? '/en/collections/all' : '/collections/all'}" class="button">
            ${this._t('empty_cta')}
          </a>
        </div>`;
      return;
    }

    container.innerHTML = this.items.map(p => this._renderCard(p)).join('');
  }

  _renderCard(p) {
    const price      = p.price ? this._formatPrice(p.price) : '';
    const isEn       = this.locale.startsWith('en');
    const productUrl = isEn && p.url && !p.url.startsWith('/en') ? `/en${p.url}` : p.url;

    const cartIcon = `
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
        <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        <line x1="3" y1="6" x2="21" y2="6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        <path d="M16 10a4 4 0 0 1-8 0" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;

    const trashIcon = `
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
        <polyline points="3 6 5 6 21 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M10 11v6M14 11v6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;

    return `
      <div class="card-wrapper product-card-wrapper underline-links-hover">
        <div class="card card--card card--media color-scheme-2 gradient">

          <div class="card__inner ratio" style="--ratio-percent: 100%;">
            <div class="card__media">
              <div class="media media--transparent media--hover-effect">
                <img src="${p.image || ''}" alt="${p.title}" class="motion-reduce" width="533" height="533" loading="lazy">
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading">
                  <a href="${productUrl}" class="full-unstyled-link">${p.title}</a>
                </h3>
              </div>
              <div class="card__badge bottom left"></div>
            </div>
          </div>

          <div class="card__content">
            <div class="card__information">
              <h3 class="card__heading h5">
                <a href="${productUrl}" class="full-unstyled-link">${p.title}</a>
              </h3>
              <div class="card-information">
                ${p.vendor ? `<div class="caption-with-letter-spacing light">${p.vendor}</div>` : ''}
                <div class="price">
                  <div class="price__container">
                    <span class="price-item price-item--regular">${price}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="card__badge bottom left wishlist-card__actions">
              ${p.variantId
                ? `<button class="wishlist-card__atc" data-wishlist-atc data-variant-id="${p.variantId}" aria-label="${this._t('add_to_cart')}">${cartIcon}</button>`
                : `<a href="${productUrl}" class="wishlist-card__atc" aria-label="${this._t('add_to_cart')}">${cartIcon}</a>`
              }
              <button class="wishlist-card__remove" data-wishlist-remove="${p.id}" aria-label="${this._t('remove')}">${trashIcon}</button>
            </div>
          </div>

        </div>
      </div>`;
  }

  /* ─── Init ────────────────────────────────────────────────── */

  init() {
    this._updateCountBadges();
    this._updateAllButtons();
    this._renderWishlistPage();

    document.addEventListener('wishlist:change', () => {
      this._updateAllButtons();
      this._renderWishlistPage();
    });
  }
}

// Bootstrap
const wishlist = new PolyreelsWishlist();
document.addEventListener('DOMContentLoaded', () => wishlist.init());
window.PolyreelsWishlist = wishlist;
